import React from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";

import { TEACHING_MATERIAL_KEYWORDS } from "../../../constants/teaching_materials";
import {
  Context,
  fetchSimpleTeachingMaterials,
  notFoundPath,
  fetchTemporaryToken,
  clearTemporaryToken,
  classDataPath,
  API_URL,
  FETCH_TEMPORARY_TOKEN_FAILURE,
} from "../../../teacherStore";
import useWaitApiCall from "../../../hooks/useWaitApiCall";
import DownloadLeafPdf from "../../../templates/teacher/DownloadLeafPdf";

export default () => {
  const { state, dispatch } = React.useContext(Context);
  const [calling, peep] = useWaitApiCall(state);
  const params = useParams<{
    class_id: string;
  }>();
  const history = useHistory();
  const { state: locationState } = useLocation<{
    nameClass: string;
  }>();
  const class_id = parseInt(params.class_id);
  const nameClass = locationState?.nameClass;

  const [pendingLesson, setPendingLesson] = React.useState<{
    lessonId: number;
    roundNo: number;
  } | null>(null);
  const pendingWindowRef = React.useRef<Window | null>(null);

  const leafItems = React.useMemo(
    () =>
      state.simple_teaching_materials.flatMap(m => {
        const leafLesson = m.lessons.find(l => l.is_leaf);
        if (!leafLesson) return [];
        return [
          {
            name: m.name,
            title: m.title,
            lessonId: leafLesson.id,
            maxRoundNo: leafLesson.max_round_no,
            package_version: m.package_version,
          },
        ];
      }),
    [state.simple_teaching_materials],
  );

  const middleItems = React.useMemo(
    () =>
      leafItems.filter(item =>
        item.title.includes(TEACHING_MATERIAL_KEYWORDS.MIDDLE),
      ),
    [leafItems],
  );

  const highItems = React.useMemo(
    () =>
      leafItems.filter(item =>
        item.title.includes(TEACHING_MATERIAL_KEYWORDS.HIGH),
      ),
    [leafItems],
  );

  const otherItems = React.useMemo(
    () =>
      leafItems.filter(
        item =>
          !item.title.includes(TEACHING_MATERIAL_KEYWORDS.MIDDLE) &&
          !item.title.includes(TEACHING_MATERIAL_KEYWORDS.HIGH),
      ),
    [leafItems],
  );

  React.useEffect(() => {
    dispatch(peep(fetchSimpleTeachingMaterials(class_id)));
  }, [class_id, peep, dispatch]);

  React.useEffect(() => {
    if (!nameClass) {
      history.replace(notFoundPath);
    }
  }, [history, nameClass]);

  React.useEffect(() => {
    if (state.temporaryToken && pendingLesson !== null) {
      if (pendingWindowRef.current) {
        pendingWindowRef.current.location.href = `${API_URL}/lessons/${pendingLesson.lessonId}/leaf_pdf?klass_id=${class_id}&round_no=${pendingLesson.roundNo}&temporary_token=${state.temporaryToken}`;
        pendingWindowRef.current = null;
      }
      dispatch(clearTemporaryToken());
      setPendingLesson(null);
    }
  }, [state.temporaryToken, pendingLesson, class_id, dispatch]);

  React.useEffect(() => {
    if (
      state.lastAction === FETCH_TEMPORARY_TOKEN_FAILURE &&
      pendingWindowRef.current
    ) {
      pendingWindowRef.current.close();
      pendingWindowRef.current = null;
      setPendingLesson(null);
    }
  }, [state.lastAction]);

  const goBack = React.useCallback(
    () =>
      history.push(`${classDataPath}/${class_id}`, {
        nameClass,
      }),
    [class_id, history, nameClass],
  );

  const handleDownloadLesson = React.useCallback(
    (lessonId: number, roundNo: number) => {
      pendingWindowRef.current = window.open("", "_blank");
      setPendingLesson({ lessonId, roundNo });
      dispatch(peep(fetchTemporaryToken()));
    },
    [dispatch, peep],
  );

  return nameClass ? (
    <DownloadLeafPdf
      calling={calling}
      middleItems={middleItems}
      highItems={highItems}
      otherItems={otherItems}
      goBack={goBack}
      onDownloadLesson={handleDownloadLesson}
    />
  ) : null;
};
